/**
 * PDP delivery date picker.
 *
 * The shopper types a UK postcode; this asks the ported rules engine
 * (assets/jjfo-delivery-rules.js) exactly what the checkout would ask, and
 * renders the answer as a calendar. The chosen date is written into hidden
 * line-item-property inputs that ride along with the product form.
 *
 * Deliberately mirrors the checkout extension's behaviour where it can:
 * auto-selecting the earliest Joseph James date, nudging a Proovia buyer
 * towards the nearest full-service one, and the free-storage opt-out.
 */
if (!customElements.get('jjfo-delivery-picker')) {
  customElements.define(
    'jjfo-delivery-picker',
    class JjfoDeliveryPicker extends HTMLElement {
      connectedCallback() {
        this.engine = window.JJFODeliveryRules;
        if (!this.engine) {
          console.error('[jjfo-delivery-picker] rules engine asset did not load');
          return;
        }

        // Moving a block in the theme editor can disconnect and reconnect the
        // same element. Its child listeners are still attached from last time,
        // so only the document-level one — the single thing disconnectedCallback
        // tears down — is put back, rather than binding everything twice.
        if (this.initialised) {
          document.addEventListener('submit', this.submitCaptureHandler, true);
          this.applyGate();
          return;
        }

        this.productTags = this.parseTags(this.dataset.productTags);
        this.sectionId = this.dataset.sectionId;
        this.requireDate = this.dataset.requireDate === 'true';
        this.upsellEnabled = this.dataset.showUpsell === 'true';
        this.storageMonths = Number(this.dataset.storageMonths) || this.engine.CONFIG.storageMonths;

        this.form = document.getElementById(this.dataset.formId);
        this.submitButton = document.getElementById('ProductSubmitButton-' + this.sectionId);

        // Heading, intro, postcode field and the free-delivery line move as one
        // unit, so the results view swaps them all out with a single write.
        this.introEl = this.querySelector('[data-ddp-intro]');
        this.postcodeForm = this.querySelector('[data-ddp-postcode-form]');
        this.postcodeInput = this.querySelector('[data-ddp-postcode-input]');
        this.errorEl = this.querySelector('[data-ddp-error]');
        this.resultsEl = this.querySelector('[data-ddp-results]');
        this.postcodeEcho = this.querySelector('[data-ddp-postcode-echo]');
        this.changeBtn = this.querySelector('[data-ddp-change]');
        // One <template> per courier that has a logo uploaded. A courier with
        // none — DPD always — is simply absent here, and its dates show the
        // courier dot alone.
        this.markTemplates = {};
        this.querySelectorAll('[data-ddp-mark]').forEach((template) => {
          this.markTemplates[template.dataset.ddpMark] = template;
        });
        this.monthTitle = this.querySelector('[data-ddp-month-title]');
        this.prevBtn = this.querySelector('[data-ddp-prev]');
        this.nextBtn = this.querySelector('[data-ddp-next]');
        this.daysEl = this.querySelector('[data-ddp-days]');
        this.chosenCard = this.querySelector('[data-ddp-chosen]');
        this.chosenTitle = this.querySelector('[data-ddp-chosen-title]');
        this.chosenBody = this.querySelector('[data-ddp-chosen-body]');
        // Empty when the merchant has uploaded neither logo — renderChoice then
        // has nothing to toggle and the card renders exactly as it used to.
        this.courierLogos = Array.from(this.querySelectorAll('[data-ddp-courier-logo]'));
        this.upsellCard = this.querySelector('[data-ddp-upsell]');
        this.upsellBody = this.querySelector('[data-ddp-upsell-body]');
        this.upsellBtn = this.querySelector('[data-ddp-upsell-btn]');
        this.storageCheckbox = this.querySelector('[data-ddp-storage]');
        this.storageCopy = this.querySelector('[data-ddp-storage-copy]');
        this.requiredNotice = this.querySelector('[data-ddp-required-notice]');

        this.inputs = {
          date: this.querySelector('[data-ddp-prop="date"]'),
          courier: this.querySelector('[data-ddp-prop="courier"]'),
          service: this.querySelector('[data-ddp-prop="service"]'),
          iso: this.querySelector('[data-ddp-prop="iso"]'),
          choice: this.querySelector('[data-ddp-prop="choice"]'),
        };

        this.options = null;
        this.optionsByDate = new Map();
        this.selectedDate = '';
        this.wantsStorage = false;
        this.postcode = '';

        if (this.storageCopy) {
          this.storageCopy.textContent = this.storageCopy.dataset.template.replace(
            '[months]',
            String(this.storageMonths)
          );
        }

        this.bindEvents();
        // Nothing has been chosen yet, so this writes the "no choice" fallback.
        // The snippet already pre-fills it — this is what keeps the inputs
        // right if that default and this setting ever drift apart.
        this.persist();
        this.applyGate();
        this.initialised = true;
      }

      disconnectedCallback() {
        if (this.unsubscribeVariantChange) this.unsubscribeVariantChange();
        if (this.submitCaptureHandler) {
          document.removeEventListener('submit', this.submitCaptureHandler, true);
        }
      }

      bindEvents() {
        this.postcodeForm.addEventListener('submit', (event) => {
          event.preventDefault();
          this.lookup();
        });

        this.changeBtn.addEventListener('click', () => {
          this.resultsEl.hidden = true;
          this.introEl.hidden = false;
          this.postcodeInput.focus();
          this.clearChoice();
        });

        this.prevBtn.addEventListener('click', () => this.shiftMonth(-1));
        this.nextBtn.addEventListener('click', () => this.shiftMonth(1));

        this.storageCheckbox.addEventListener('change', () => {
          this.wantsStorage = this.storageCheckbox.checked;
          if (this.wantsStorage) this.selectedDate = '';
          this.renderDays();
          this.renderChoice();
          this.persist();
        });

        this.upsellBtn.addEventListener('click', () => {
          if (!this.jjfoAlternative) return;
          // Move the month cursor first: the nearest full-service date can sit
          // in a month the shopper isn't looking at, and updating the cards
          // while the calendar still shows the old month reads as broken.
          this.monthCursor = this.jjfoAlternative.slice(0, 7);
          this.selectDate(this.jjfoAlternative);
        });

        // The theme re-applies the Add to Cart button's native disabled state
        // from freshly fetched HTML on every variant change
        // (assets/product-info.js), so re-assert the gate afterwards.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            this.submitButton = document.getElementById('ProductSubmitButton-' + this.sectionId);
            this.applyGate();
          });
        }

        // Backstop for the gate. `aria-disabled` alone is already honoured by
        // assets/product-form.js, but a capture-phase listener on the document
        // is guaranteed to run before the form's own handler whatever else is
        // bound to it.
        this.submitCaptureHandler = (event) => {
          if (event.target !== this.form) return;
          if (!this.isBlocked()) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.revealRequiredNotice();
        };
        document.addEventListener('submit', this.submitCaptureHandler, true);
      }

      parseTags(raw) {
        if (!raw) return [];
        try {
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          console.error('[jjfo-delivery-picker] could not read product tags', error);
          return [];
        }
      }

      lookup() {
        const raw = this.postcodeInput.value;

        // Nothing typed is a different problem from something mistyped, and
        // saying "that doesn't look like a UK postcode" about an empty box
        // reads as though the shopper got it wrong when they simply haven't
        // filled it in yet.
        if (String(raw).trim() === '') {
          this.showError(this.dataset.errorEmpty);
          this.postcodeInput.focus();
          return;
        }

        const parsed = this.engine.parsePostcode(raw);

        // Only the SHAPE is validated here. An unrecognised area is a perfectly
        // good postcode the engine handles on its own (it falls back to the
        // Proovia-only pathway), so it must not be rejected as a typo.
        if (!parsed) {
          this.showError(this.dataset.errorInvalid);
          this.postcodeInput.focus();
          return;
        }

        this.hideError();
        this.postcode = parsed.normalized;
        this.postcodeInput.value = parsed.normalized;

        try {
          this.options = this.engine.getDeliveryOptions({
            postcode: this.postcode,
            // One product, so the engine's cart-wide small/large rule is applied
            // to this item alone: `small` and not `large` gets DPD, anything
            // else (including untagged) follows large-item routing.
            cartItems: [{ tags: this.productTags }],
          });
        } catch (error) {
          console.error('[jjfo-delivery-picker] could not compute delivery options', error);
          this.showError(this.dataset.errorFailure);
          return;
        }

        this.optionsByDate = new Map(this.options.map((option) => [option.date, option]));

        // Anchored to UK wall-clock time, matching the engine, rather than the
        // shopper's device timezone — otherwise the calendar's idea of "today"
        // can disagree with the dates the engine just handed back.
        const todayIso = UK_TODAY_FORMAT.format(new Date());
        this.todayIso = todayIso;
        this.minMonth = todayIso.slice(0, 7);
        this.maxIso = this.engine.addDays(todayIso, this.engine.CONFIG.bookingWindowDays);
        this.maxMonth = this.maxIso.slice(0, 7);

        // Open on the first month that actually has an available date rather
        // than always the current one — near month-end, lead times can push
        // every available date into next month, and opening on an all-disabled
        // grid gives no cue that there is anything worth clicking next month.
        this.monthCursor = this.options.length ? this.options[0].date.slice(0, 7) : this.minMonth;

        // Suggest the earliest Joseph James date, as the checkout does. If this
        // address gets no JJFO date at all (Proovia-only, or a small DPD item),
        // nothing is suggested and the shopper chooses for themselves.
        this.wantsStorage = false;
        this.storageCheckbox.checked = false;
        const earliestJjfo = this.options.find((option) => option.courier === 'jjfo');
        this.selectedDate = earliestJjfo ? earliestJjfo.date : '';

        this.postcodeEcho.textContent = this.postcode;
        this.introEl.hidden = true;
        this.resultsEl.hidden = false;

        this.renderMonth();
        this.renderChoice();
        this.persist();
      }

      showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
        this.postcodeInput.setAttribute('aria-invalid', 'true');
      }

      hideError() {
        this.errorEl.hidden = true;
        this.postcodeInput.removeAttribute('aria-invalid');
      }

      shiftMonth(delta) {
        const parts = this.monthCursor.split('-').map(Number);
        const shifted = new Date(parts[0], parts[1] - 1 + delta, 1);
        this.monthCursor =
          shifted.getFullYear() + '-' + String(shifted.getMonth() + 1).padStart(2, '0');
        this.renderMonth();
      }

      renderMonth() {
        const parts = this.monthCursor.split('-').map(Number);
        this.monthTitle.textContent = new Intl.DateTimeFormat('en-GB', {
          month: 'long',
          year: 'numeric',
        }).format(new Date(parts[0], parts[1] - 1, 1));

        this.prevBtn.disabled = this.monthCursor <= this.minMonth;
        this.nextBtn.disabled = this.monthCursor >= this.maxMonth;

        this.renderDays();
      }

      /** Monday-first cells for the month, with leading/trailing blanks. */
      monthCells() {
        const parts = this.monthCursor.split('-').map(Number);
        const year = parts[0];
        const month = parts[1];
        const daysInMonth = new Date(year, month, 0).getDate();
        const startWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7;

        const cells = new Array(startWeekday).fill(null);
        for (let day = 1; day <= daysInMonth; day++) {
          cells.push(this.monthCursor + '-' + String(day).padStart(2, '0'));
        }
        while (cells.length % 7 !== 0) cells.push(null);
        return cells;
      }

      renderDays() {
        const fragment = document.createDocumentFragment();

        this.monthCells().forEach((iso) => {
          const cell = document.createElement('li');

          if (!iso) {
            cell.className = 'jjfo-ddp__day--blank';
            cell.setAttribute('aria-hidden', 'true');
            fragment.appendChild(cell);
            return;
          }

          const option = this.optionsByDate.get(iso);
          const selectable = Boolean(option) && !this.wantsStorage;

          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'jjfo-ddp__day';
          if (selectable) {
            button.classList.add('jjfo-ddp__day--available');
            // Which accent the cell's hover, focus and selected states draw in.
            // Proovia and DPD share one: both are the partner-courier service,
            // and no pathway ever offers both.
            button.classList.add(
              option.courier === 'jjfo' ? 'jjfo-ddp__day--jjfo' : 'jjfo-ddp__day--partner'
            );
          }
          button.disabled = !selectable;
          button.setAttribute('aria-pressed', String(iso === this.selectedDate));

          const num = document.createElement('span');
          num.className = 'jjfo-ddp__day-num';
          num.textContent = String(Number(iso.slice(8, 10)));
          button.appendChild(num);

          // The logo slot, which CSS reveals on hover and keeps once the date is
          // chosen. Appended to every cell, empty ones included, so all cells
          // are the same height and revealing a logo cannot shift the grid.
          //
          // Cloned from the Liquid template rather than built here, so the image
          // keeps Shopify's srcset. Its alt is empty by design: the aria-label
          // below already names the courier, and a described image would have
          // the cell announce it twice.
          //
          // Each cell shows the logo of whichever courier would actually bring
          // it: the real courier is shown from the moment the user hovers,
          // so browsing the grid gives an accurate picture of what is
          // available. JJ dates show the JJ mark, Proovia dates show the
          // Proovia mark on hover and keep it once chosen, and DPD dates show
          // no mark at all — same as the "no template" case in the Liquid
          // above, since markTemplates has no 'dpd' entry.
          //
          // Safe to decide here rather than in CSS because selecting a date
          // re-renders the whole grid (selectDate -> renderMonth -> renderDays),
          // so this runs again for every cell on every change of selection.
          const markCourier = option ? option.courier : null;
          const mark = document.createElement('span');
          mark.className = 'jjfo-ddp__day-mark';
          const markTemplate = selectable ? this.markTemplates[markCourier] : null;
          if (markTemplate) mark.appendChild(markTemplate.content.cloneNode(true));
          button.appendChild(mark);

          // Green on every deliverable date whichever courier brings it, red on
          // the rest. Keyed off the date's own courier, not off `selectable`, so
          // ticking the storage opt-out mutes the grid without repainting every
          // cell red as though the dates had gone away.
          const dot = document.createElement('span');
          dot.className = 'jjfo-ddp__dot' + (option ? '' : ' jjfo-ddp__dot--unavailable');
          button.appendChild(dot);

          button.setAttribute(
            'aria-label',
            option
              ? formatDate(iso) + ' — ' + COURIER_LABEL[option.courier]
              : formatDate(iso) + ' — unavailable'
          );

          if (selectable) {
            button.addEventListener('click', () => {
              // Clicking the selected date clears it, matching checkout.
              this.selectDate(iso === this.selectedDate ? '' : iso);
            });
          }

          cell.appendChild(button);
          fragment.appendChild(cell);
        });

        this.daysEl.classList.toggle('jjfo-ddp__days--muted', this.wantsStorage);
        this.daysEl.replaceChildren(fragment);
      }

      selectDate(iso) {
        this.selectedDate = iso;
        if (iso) {
          this.wantsStorage = false;
          this.storageCheckbox.checked = false;
        }
        this.renderMonth();
        this.renderChoice();
        this.persist();
      }

      clearChoice() {
        this.selectedDate = '';
        this.wantsStorage = false;
        this.storageCheckbox.checked = false;
        this.options = null;
        this.optionsByDate = new Map();
        this.renderChoice();
        this.persist();
      }

      renderChoice() {
        const option = this.selectedDate ? this.optionsByDate.get(this.selectedDate) : null;

        // Show the logo for the courier actually delivering this date, if one
        // was uploaded for it. Nothing matches when there is no selection, so
        // this also clears the logo when the choice is cleared.
        const courier = option ? option.courier : '';
        this.courierLogos.forEach((logo) => {
          logo.hidden = logo.dataset.ddpCourierLogo !== courier;
        });

        if (option) {
          // The short wording, which is display only — persist() keeps writing
          // the full date and courier name to the line item. See formatDateDisplay.
          this.chosenTitle.textContent =
            'Arriving ' +
            formatDateDisplay(this.selectedDate) +
            ', with ' +
            COURIER_DISPLAY[option.courier];
          this.chosenBody.textContent = option.serviceLevel;
          this.chosenCard.hidden = false;
        } else {
          this.chosenCard.hidden = true;
        }

        // Nudge a Proovia buyer towards the NEAREST Joseph James date rather
        // than simply the earliest: they picked their day for a reason, so the
        // closest swap is the one most likely to be acceptable. Ties go to the
        // earlier date — same inconvenience, sooner delivery.
        this.jjfoAlternative = null;
        if (this.upsellEnabled && option && option.courier === 'proovia') {
          let nearest = null;
          this.optionsByDate.forEach((candidate) => {
            if (candidate.courier !== 'jjfo') return;
            if (
              nearest === null ||
              daysApart(candidate.date, this.selectedDate) < daysApart(nearest, this.selectedDate)
            ) {
              nearest = candidate.date;
            }
          });
          this.jjfoAlternative = nearest;
        }

        if (this.jjfoAlternative) {
          this.upsellBody.textContent =
            'The nearest full-service date is ' + formatDateDisplay(this.jjfoAlternative) + '.';
          this.upsellBtn.textContent = 'Choose ' + formatDateDisplay(this.jjfoAlternative);
          this.upsellCard.hidden = false;
        } else {
          this.upsellCard.hidden = true;
        }

        this.applyGate();
      }

      /**
       * Writes the choice into the hidden inputs.
       *
       * Courier, service and the ISO date are kept `disabled` until there is
       * something real to send — the same technique assets/recipient-form.js
       * uses — so a blank property is never created on the line item. The date
       * field is different: it always has something to say, including that
       * nothing was chosen. See the third branch.
       */
      persist() {
        const option = this.selectedDate ? this.optionsByDate.get(this.selectedDate) : null;

        // Storage records the date field only. Courier and service are left
        // unsent rather than filled with the storage blurb: nothing has been
        // routed yet, and a service level nobody has committed to would read on
        // the order as though one had been.
        if (this.wantsStorage) {
          this.setProp('date', this.dataset.storagePropertyValue);
          this.setProp('courier', '');
          this.setProp('service', '');
          this.setProp('iso', '');
          this.setChoiceProp('storage', '');
          return;
        }

        if (option) {
          this.setProp('date', formatDate(this.selectedDate));
          this.setProp('courier', COURIER_LABEL[option.courier]);
          this.setProp('service', option.serviceLevel);
          this.setProp('iso', this.selectedDate);
          this.setChoiceProp('date', this.selectedDate);
          return;
        }

        // Nothing chosen — neither a date nor the storage opt-out. The date
        // property is still sent, saying exactly that, so the order records the
        // shopper's non-choice rather than staying silent about delivery. A
        // silent line item is ambiguous: it looks the same as one from before
        // this block existed, or one where the script never ran.
        //
        // Blanking the setting in the theme editor switches this off — setProp
        // disables an input it has no value for — which is the escape hatch if
        // the merchant would rather no-choice line items carried no property.
        this.setProp('date', this.dataset.noSelectionPropertyValue);
        this.setProp('courier', '');
        this.setProp('service', '');
        this.setProp('iso', '');
        this.setChoiceProp('none', '');
      }

      setChoiceProp(choice, iso) {
        const input = this.inputs.choice;
        if (!input) return;
        input.value = JSON.stringify({choice: choice, iso: iso, postcode: this.postcode});
        input.disabled = false;
      }

      setProp(key, value) {
        const input = this.inputs[key];
        if (!input) return;
        // Coerced, because a missing data- attribute reads as undefined and
        // would otherwise be sent to the order as the literal text "undefined".
        const next = value == null ? '' : String(value);
        input.value = next;
        input.disabled = next === '';
      }

      /** A choice has been made: either a real date, or the storage opt-out. */
      hasChoice() {
        return this.wantsStorage || Boolean(this.selectedDate && this.optionsByDate.has(this.selectedDate));
      }

      isBlocked() {
        return this.requireDate && !this.hasChoice();
      }

      /**
       * Gates Add to Cart via `aria-disabled`, which assets/product-form.js
       * already refuses to submit through.
       *
       * Deliberately NOT the native `disabled` attribute: the theme owns that
       * one for sold-out and no-variant states (snippets/buy-buttons.liquid)
       * and re-applies it from fetched HTML on variant change
       * (assets/product-info.js), so writing to it would both fight that logic
       * and risk re-enabling a genuinely unavailable product.
       */
      applyGate() {
        if (!this.requireDate || !this.submitButton) return;

        if (this.isBlocked()) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
          if (this.requiredNotice) this.requiredNotice.hidden = true;
        }
      }

      revealRequiredNotice() {
        if (!this.requiredNotice) return;
        this.requiredNotice.hidden = false;
        // Guarded: this runs from a submit handler, so an environment without
        // scrollIntoView must not take the whole gate down with it.
        if (typeof this.requiredNotice.scrollIntoView === 'function') {
          this.requiredNotice.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  );
}

const UK_TODAY_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * The formal names. These are written to the order by persist() and are also
 * what a day cell's aria-label announces, so they stay unabbreviated.
 */
const COURIER_LABEL = {
  jjfo: 'Joseph James Delivery Service',
  proovia: 'Proovia Courier',
  dpd: 'DPD',
};

/** The same couriers as they appear on screen, where the card is already
 *  captioned by its logo and the full legal name would only crowd the line. */
const COURIER_DISPLAY = {
  jjfo: 'Joseph James',
  proovia: 'Proovia',
  dpd: 'DPD',
};

/**
 * Formatted in UTC, not the shopper's timezone. `new Date('2026-08-02')` parses
 * as UTC midnight, so formatting it in any timezone behind UTC reports the
 * previous day. These are calendar dates, not instants, so they must be read
 * back out in the frame they were written in.
 *
 * The year is included, where the checkout's equivalent omits it: this string
 * is also what gets stored on the line item and read back off the order, and
 * the booking window runs 90 days out, so "Saturday 3 January" alone would be
 * ambiguous on an order placed in December.
 */
function formatDate(isoDate) {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(isoDate + 'T00:00:00Z'));
}

/**
 * The wording shown on screen: weekday, day and month, no year.
 *
 * Deliberately NOT formatDate above, and the difference is not cosmetic. This
 * string is read in the moment, beside a calendar whose header already names
 * the month and year, so the year is noise. formatDate's output is read months
 * later off the order by whoever is loading the van, where the 90-day booking
 * window makes a bare "Friday 3 January" ambiguous on anything placed in
 * December. Two audiences, two strings — persist() must keep calling formatDate.
 */
function formatDateDisplay(isoDate) {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(isoDate + 'T00:00:00Z'));
}

/**
 * Whole days between two ISO dates, unsigned. Elapsed milliseconds is safe
 * here because both sides are anchored at UTC midnight, so the difference is
 * always an exact multiple of a day with no clock change to absorb.
 */
function daysApart(isoA, isoB) {
  const MS_PER_DAY = 86400000;
  return Math.abs(Date.parse(isoA + 'T00:00:00Z') - Date.parse(isoB + 'T00:00:00Z')) / MS_PER_DAY;
}
